/**
 * KycGateBlocker — Iter 154
 *
 * Full-screen blocker rendered for buyers/sellers (legal account owners)
 * whose `GET /me/kyc-gate` returns `required: true`. Renders the same
 * `<KycVerificationPanel/>` they would see in Settings but locks the rest
 * of the portal until KYC.status flips to SUCCESS.
 *
 * Policy (set by user · Iter 154):
 *   · Hard gate after onboarding (1a).
 *   · Demo accounts auto-pass via backend (3a).
 *   · Legacy users grandfathered for portal entry — backend returns
 *     `required: false, grandfathered: true` for them (4a). Money
 *     endpoints still 403 with reason=kyc_required.
 *   · Team operators inherit from the owner — backend handles (5a).
 *   · Buyers with no business details yet are redirected to that tab
 *     first (2a + `business_required` flag from backend).
 */
import { useEffect, useState } from "react";
import { ShieldCheck, ArrowRight } from "lucide-react";
import KycVerificationPanel from "./KycVerificationPanel";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KycGateBlocker({ children, roleHint }) {
  const { user, token } = useAuth();
  const [state, setState]     = useState(null);  // {required, reason, kyc_status, business_required}
  const [loaded, setLoaded]   = useState(false);

  async function load() {
    if (!token) { setLoaded(true); return; }
    try {
      const r = await fetch(`${API}/me/kyc-gate`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const j = await r.json();
        setState(j);
      } else {
        // If the endpoint errors, fail-open (don't block portal access).
        setState({ required: false, reason: "gate_unreachable" });
      }
    } catch (e) {
      setState({ required: false, reason: "gate_network_error" });
    } finally {
      setLoaded(true);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  // Light auto-poll while gated — flips closed automatically when KYC succeeds.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!state?.required) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [state?.required]);

  // While initial check is loading, render children (avoids flash of blocker
  // for the 200ms gate fetch). Acceptable risk — the API endpoints stay
  // gated server-side regardless.
  if (!loaded || !state?.required) return children;

  // Detect role for the headline copy. We don't *need* to know the role
  // since the same panel works for both; this just tunes the messaging.
  const role = (roleHint || user?.role || "buyer").toLowerCase();

  return (
    <div
      className="fixed inset-0 z-[100] bg-foreground/95 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-12 px-4"
      data-testid="kyc-gate-blocker"
    >
      <div className="w-full max-w-2xl bg-background rounded-3xl border-2 border-border shadow-2xl p-8 md:p-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-foreground text-background grid place-items-center">
            <ShieldCheck className="w-5 h-5"/>
          </div>
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
              Final step
            </div>
            <h1 className="display text-2xl tracking-tight font-semibold">
              One more thing — verify to unlock Verqo
            </h1>
          </div>
        </div>

        <p className="text-[13.5px] text-foreground/65 leading-relaxed mb-6">
          {role === "seller"
            ? "Before you can receive payouts or move money on Verqo, we need to verify your business identity and link your operating bank. This protects your buyers, satisfies our banking partners, and unlocks instant payouts."
            : "Before you can fund your wallet, pay invoices, or place orders on Verqo, we need to verify your business identity and link your operating bank. This protects your sellers, satisfies our banking partners, and unlocks instant credit."}
        </p>

        {state.business_required ? (
          <div
            className="rounded-2xl border-2 border-amber-300 bg-amber-50/60 p-5 mb-2"
            data-testid="kyc-gate-business-required"
          >
            <div className="text-[14px] font-semibold mb-1">
              Tell us about your business first
            </div>
            <p className="text-[12.5px] text-foreground/70 leading-snug mb-3">
              We need your legal name and address before we can run the
              identity & bank verification.
            </p>
            <a
              href={role === "seller" ? "/seller/settings#business" : "/buyer/settings#business"}
              className="inline-flex items-center gap-1.5 text-[12.5px] mono uppercase tracking-wider font-medium text-foreground hover:underline"
              data-testid="kyc-gate-business-link"
            >
              Complete business details <ArrowRight className="w-3.5 h-3.5"/>
            </a>
          </div>
        ) : (
          <div data-testid="kyc-gate-panel-wrapper">
            <KycVerificationPanel/>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-border text-[11px] mono uppercase tracking-wider text-foreground/45">
          You can&apos;t access the rest of Verqo until verification is complete.
        </div>
      </div>
    </div>
  );
}
